// Bilingual Research Data - Japanese PRIMARY, English SECONDARY

export interface BilingualResearchPaper {
  id: string;
  category: 'published' | 'manuscript' | 'article';
  keywords: string[];
  link?: string;
  certificateImage?: string;
  citation: string;
  en: {
    title: string;
    authors: string;
    venue: string;
    status: string;
    note?: string;
    problem: string;
    approach: string;
    dataset: string;
    result: string;
    significance: string;
  };
  ja: {
    title: string;
    authors: string;
    venue: string;
    status: string;
    note?: string;
    problem: string;
    approach: string;
    dataset: string;
    result: string;
    significance: string;
  };
}

export const researchPapers: BilingualResearchPaper[] = [
  {
    id: "nai-ieee",
    category: 'published',
    keywords: ["EEG", "P300", "Real-time Processing", "EEGNet", "Brain-Computer Interface", "IEEE Xplore"],
    link: "https://ieeexplore.ieee.org/document/11484707",
    citation: "DOI: 10.1109/ETFI68128.2026.11484707",
    en: {
      title: "NeuroAdaptive Interface (NAI): Real-Time EEG-Based Cognitive State Monitoring Using P300 and Deep Learning",
      authors: "Anjali Barge",
      venue: "2026 International Conference on Emerging Technologies and Future Innovations (ETFI)",
      status: "Published in IEEE Xplore",
      note: "Published April 28, 2026 | Conference: February 12-14, 2026, Pune, India",
      problem: "Real-time cognitive monitoring requires sub-second EEG processing with reliable P300 detection across subjects",
      approach: "EEGNet architecture with LabStreamingLayer for real-time ERP classification",
      dataset: "ERP CORE P3b (20 subjects), Leave-One-Subject-Out evaluation",
      result: "Cross-subject AUC: 0.57 ± 0.12, within-subject AUC: 0.85-0.90, real-time latency <500ms",
      significance: "Demonstrates practical real-time BCI pipeline with reproducible implementation and realistic cross-subject performance reporting"
    },
    ja: {
      title: "NeuroAdaptive Interface (NAI): P300とディープラーニングを用いたリアルタイムEEGベース認知状態モニタリング",
      authors: "Anjali Barge",
      venue: "2026 International Conference on Emerging Technologies and Future Innovations (ETFI)",
      status: "IEEE Xplore掲載",
      note: "2026年4月28日掲載 | 学会：2026年2月12-14日、インド・プネー",
      problem: "リアルタイム認知モニタリングには、被験者間で信頼性の高いP300検出を伴うサブ秒のEEG処理が必要",
      approach: "リアルタイムERP分類のためのLabStreamingLayerを用いたEEGNetアーキテクチャ",
      dataset: "ERP CORE P3b（20被験者）、Leave-One-Subject-Out評価",
      result: "被験者間AUC: 0.57 ± 0.12、被験者内AUC: 0.85-0.90、リアルタイムレイテンシ <500ms",
      significance: "再現可能な実装と現実的な被験者間性能報告を伴う実用的なリアルタイムBCIパイプラインを実証"
    }
  },
  {
    id: "tsception-manuscript",
    category: 'manuscript',
    keywords: ["Motor Imagery", "TSception", "Cross-Subject", "LORO", "BCI Competition IV-2a"],
    link: "/TSception_MI_LORO_Anjali_Barge.pdf",
    citation: "",
    en: {
      title: "Cross-Subject Motor Imagery Classification Using TSception FilterBank Under Leave-One-Run-Out Evaluation",
      authors: "Anjali Barge",
      venue: "Independent Research Project",
      status: "Manuscript Available",
      note: "Experimental results achieved on BCI Competition IV-2a dataset. Research manuscript available upon request.",
      problem: "Cross-subject BCI generalization remains challenging for practical motor imagery applications",
      approach: "TSception FilterBank architecture with 54-fold Leave-One-Run-Out cross-validation",
      dataset: "BCI Competition IV-2a: 9 subjects × 6 runs, 288 trials per subject, 4-class MI classification",
      result: "74.0% ± 11.0% accuracy, +8.6% improvement over CSP-LDA baseline (p < 0.00001)",
      significance: "Demonstrates cross-subject classification performance and statistical significance for subject-independent BCI systems"
    },
    ja: {
      title: "Leave-One-Run-Out評価下でのTSception FilterBankを用いた被験者間運動イメージ分類",
      authors: "Anjali Barge",
      venue: "",
      status: "個人研究プロジェクト",
      note: "BCI Competition IV-2aデータセットを用いた実験結果。研究原稿はリクエストに応じて共有可能です。",
      problem: "被験者間BCI汎化は実用的な運動イメージアプリケーションにとって依然として課題",
      approach: "54回のLeave-One-Run-Outクロスバリデーションを用いたTSception FilterBankアーキテクチャ",
      dataset: "BCI Competition IV-2a: 9被験者 × 6ラン、被験者あたり288試行、4クラスMI分類",
      result: "分類精度：74.0% ± 11.0%、CSP-LDAベースラインに対して+8.6%改善（p < 0.00001）",
      significance: "被験者に依存しないBCIシステムにおける被験者間分類性能と統計的有意性を実証"
    }
  },
  {
    id: "ecofusion-review",
    category: 'article',
    keywords: ["Multimodal AI", "Biodiversity", "Satellite Imagery", "Bioacoustics", "Conservation"],
    link: "/IJRPR53952.pdf",
    citation: "Vol. 6, Issue 10, October 2025, pp. 3459–3464",
    en: {
      title: "Eco-Fusion AI: A Multimodal Framework for Habitat Monitoring",
      authors: "Akhila Ohmkumar, Anjali Barge, Neha Dhurgude, Dhanashree Jadhav, Tanvi Powar, Prof. Vrushali More",
      venue: "International Journal of Research Publication and Reviews",
      status: "Published",
      note: "Literature review and framework synthesis published October 2025",
      problem: "Biodiversity monitoring lacks integrated frameworks combining satellite and bioacoustic data",
      approach: "Systematic literature review of multimodal AI approaches for habitat assessment",
      dataset: "Comprehensive analysis of NDVI, GBIF, and BirdCLEF integration methodologies",
      result: "Framework synthesis with implementation guidelines for conservation applications",
      significance: "Provides systematic roadmap for multimodal AI deployment in conservation technology"
    },
    ja: {
      title: "Eco-Fusion AI: 生息地モニタリングのためのマルチモーダルフレームワーク",
      authors: "Akhila Ohmkumar、Anjali Barge、Neha Dhurgude、Dhanashree Jadhav、Tanvi Powar、Prof. Vrushali More",
      venue: "International Journal of Research Publication and Reviews",
      status: "掲載済み",
      note: "文献レビューとフレームワーク統合論文、2025年10月掲載",
      problem: "生物多様性モニタリングには衛星データと生物音響データを組み合わせた統合フレームワークが不足",
      approach: "生息地評価のためのマルチモーダルAIアプローチの体系的文献レビュー",
      dataset: "NDVI、GBIF、BirdCLEF統合手法の包括的分析",
      result: "保全アプリケーションのための実装ガイドラインを伴うフレームワーク統合",
      significance: "保全技術におけるマルチモーダルAI展開のための体系的ロードマップを提供"
    }
  }
];
